import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { SaveIcon } from 'lucide-react';
import { CloudArrowUpIcon } from '@heroicons/react/24/outline';

const InvestorProgressSettingsPage = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState({
        section_tag: '',
        section_heading: '',
        lead_text: '',
        image_url: '',
        overlay_value: '',
        overlay_label: ''
    });
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/admin/investor-progress/settings');
            if (response.data.success) {
                setSettings(response.data.data);
                if (response.data.data.image_url) {
                    setImagePreview(`http://localhost:8001${response.data.data.image_url}`);
                }
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
            toast.error('Failed to load settings');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setSettings(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImageFile(file);
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);

            const formData = new FormData();
            formData.append('section_tag', settings.section_tag);
            formData.append('section_heading', settings.section_heading);
            formData.append('lead_text', settings.lead_text);
            formData.append('overlay_value', settings.overlay_value);
            formData.append('overlay_label', settings.overlay_label);

            if (!imageFile) {
                formData.append('image_url', settings.image_url);
            }

            if (imageFile) {
                formData.append('image', imageFile);
            }

            await axios.put('/api/admin/investor-progress/settings', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            toast.success('Settings updated successfully');
            setImageFile(null);
            fetchSettings();
        } catch (error) {
            console.error('Error updating settings:', error);
            toast.error('Failed to update settings');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-slate-800">Our Progress - Settings</h1>
                <p className="text-slate-500 mt-1">Manage the "Our Progress" section content</p>
            </div>

            <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 shadow-sm space-y-6">
                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Section Tag</label>
                    <input
                        type="text"
                        name="section_tag"
                        value={settings.section_tag}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Our Progress"
                    />
                </div>

                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Section Heading</label>
                    <input
                        type="text"
                        name="section_heading"
                        value={settings.section_heading}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Consistent Performance, Measured Progress"
                    />
                </div>

                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Lead Text</label>
                    <textarea
                        name="lead_text"
                        value={settings.lead_text}
                        onChange={handleChange}
                        rows="3"
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="We believe in measurable progress..."
                    />
                </div>

                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Performance Image</label>
                    <p className="text-xs text-slate-500 mb-3">📐 Recommended: 800 x 600 pixels | Formats: WebP, PNG, JPEG</p>
                    <div className="flex gap-4">
                        <div className="flex-1">
                            <label className="block border-2 border-dashed border-slate-300 rounded-xl p-6 text-center cursor-pointer hover:border-primary-500 transition-colors">
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                    className="hidden"
                                />
                                <CloudArrowUpIcon className="h-12 w-12 text-slate-400 mx-auto mb-2" />
                                <p className="text-sm font-medium text-slate-600">
                                    {imagePreview ? 'Change Image' : 'Upload Image'}
                                </p>
                                <p className="text-xs text-slate-400 mt-1">Max 10MB</p>
                            </label>
                        </div>
                        {imagePreview && (
                            <div className="w-48 h-32 rounded-xl overflow-hidden border-2 border-slate-200">
                                <img
                                    src={imagePreview}
                                    alt="Preview"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Overlay Value</label>
                        <input
                            type="text"
                            name="overlay_value"
                            value={settings.overlay_value}
                            onChange={handleChange}
                            className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                            placeholder="Driven by market insights."
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Overlay Label</label>
                        <input
                            type="text"
                            name="overlay_label"
                            value={settings.overlay_label}
                            onChange={handleChange}
                            className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                            placeholder="Built for long-term advantage."
                        />
                    </div>
                </div>

                <div className="flex justify-end pt-4">
                    <button
                        type="submit"
                        disabled={saving}
                        className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <SaveIcon size={20} />
                        {saving ? 'Saving...' : 'Save Settings'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default InvestorProgressSettingsPage;
